import { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import { TPAMessageCallback } from '../types/tpaMessage';
import {
  AppData,
  DocumentServicesObject,
  DsItem,
} from '@wix/document-services-types';

export const createTpaUtils = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
) => {
  const getSelectedComponentData = async (): Promise<DsItem> => {
    const [selectedComp] =
      await platformApiMethods.editor.selection.getSelectedComponents();
    return documentServicesAPI.components.data.get(selectedComp);
  };

  const selectComponentAndNavigateIfNeeded = async (
    appData: AppData,
    callback?: TPAMessageCallback,
  ) => {
    const selectedCompData = await getSelectedComponentData();
    const selectedCompAppId = selectedCompData?.applicationId;
    if (
      !selectedCompAppId ||
      selectedCompAppId.toString() !== appData?.applicationId?.toString()
    ) {
      const data = documentServicesAPI.tpa.app.getFirstAppCompPageId(
        appData.appDefinitionId!,
        false,
      );

      if (!data) {
        throw new Error(
          `Application with appDefinitionId ${appData.appDefinitionId} was not found in the document`,
        );
      }

      const curPageId = documentServicesAPI.pages.getFocusedPageId();
      const compPointer = documentServicesAPI.components.get.byId(
        data.compId,
        data.pageId,
      );

      if (!compPointer) {
        throw new Error(
          `Component with compId ${data.compId} was not found in the document`,
        );
      }

      if (data.pageId !== curPageId && data.pageId !== 'masterPage') {
        documentServicesAPI.pages.navigateTo(data.pageId, function () {
          platformApiMethods.editor.selection.selectComponentByCompRef(
            {} as AppData,
            '',
            {
              compsToSelect: [compPointer],
            },
          );
          if (callback) {
            callback();
          }
        });
      } else {
        platformApiMethods.editor.selection.selectComponentByCompRef(
          {} as AppData,
          '',
          {
            compsToSelect: [compPointer],
          },
        );

        if (callback) {
          callback();
        }
      }
    } else if (callback) {
      callback();
    }
  };

  const isSiteInDraftMode = () => {
    return documentServicesAPI.generalInfo.isDraft();
  };

  const isSiteSaved = () => {
    return (
      // @ts-expect-error add isFirstSave to DM types
      !documentServicesAPI.generalInfo.isFirstSave() && !isSiteInDraftMode()
    );
  };

  const joinURL = (...parts: string[]) => {
    const TRAILING_SLASH_REGEX = /\/+$/;
    const LEADING_AND_TRAILING_SLASHES_REGEX = /^\/+|\/+$/g;

    return parts
      .filter(Boolean)
      .map((part, i) =>
        i === 0
          ? part.replace(TRAILING_SLASH_REGEX, '')
          : part.replace(LEADING_AND_TRAILING_SLASHES_REGEX, ''),
      )
      .join('/');
  };

  const getBusinessManagerUrl = () => {
    const isSaved = isSiteSaved();
    const isDraftMode = isSiteInDraftMode();

    if (!isSaved || isDraftMode) {
      throw new Error('Site has never been saved');
    }

    const metaSiteId = documentServicesAPI.generalInfo.getMetaSiteId();
    return joinURL(
      window.serviceTopology.businessManagerDomain,
      'dashboard',
      metaSiteId,
    );
  };

  return {
    getBusinessManagerUrl,
    selectComponentAndNavigateIfNeeded,
    joinURL,
  };
};
